// ./src/components/features/VolunteerRoles/VolunteerRoleEditDrawer/VolunteerRoleEditDrawer.tsx
//
// Create/edit drawer for a VolunteerRole (account-level reusable role). Self-loading
// in edit mode via recordId; context-or-props resolution; no success toast (the page
// highlight is the confirmation). Fields: name, summary, responsibilities, eligibility
// (markdown), icon, color, status, and the hourly rate (#A4.7 staff pay — empty = an unpaid volunteer; paid in each
// event's currency, since a role is account-wide and events may differ).

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Textarea, Group, Divider, Skeleton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { VolunteerRole, VolunteerRoleInput } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalVolunteerRolesContext } from "@/contexts/VolunteerRolesContext";
import { useCurrentEvent } from "@/contexts";
import { AppDrawer, StatusInput, IconSelector, ColorPickerInput } from "@/components/generic";
import { MoneyInput } from "@/components/features/Budget/MoneyInput/MoneyInput";

export interface VolunteerRoleEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode (e.g. from a "Create X" action). */
  initialName?: string | null;
  onCreated?: (role: VolunteerRole) => void;
  onUpdated?: (role: VolunteerRole) => void;
}

interface FormData {
  name: string;
  summary: string;
  responsibilities: string;
  eligibility: string;
  icon: string;
  color: string;
  hourlyRate: number | null;
  status: number;
}

const KNOWN_FORM_FIELDS = ["name", "summary", "responsibilities", "eligibility", "icon", "color", "hourlyRate", "status"];

const getInitialFormData = (): FormData => ({
  name: "",
  summary: "",
  responsibilities: "",
  eligibility: "",
  icon: "",
  color: "",
  hourlyRate: null,
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: VolunteerRole): FormData => ({
  name: record.name || "",
  summary: record.summary || "",
  responsibilities: record.responsibilities || "",
  eligibility: record.eligibility || "",
  icon: record.icon || "",
  color: record.color || "",
  hourlyRate: record.hourlyRate ?? null,
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const VolunteerRoleEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={120} radius="sm" />
  </Stack>
);

export const VolunteerRoleEditDrawer: React.FC<VolunteerRoleEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["volunteerRoles", "common"]);
  // Opened from inside an event (create-in-place from a shift) → show that event's currency; from the library → none.
  const { currentEvent } = useCurrentEvent();
  const eventCurrency = currentEvent?.currencyCode ?? null;

  const ctx = useOptionalVolunteerRolesContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<VolunteerRole | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as VolunteerRole);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  // Own submit result first — a context `lastError` alone swallows errors when the caller saves another way.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  const effectiveError = loadError || submitError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record
      ? fromRecord(record)
      : { ...getInitialFormData(), name: initialName?.trim() || "" };
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    setSubmitError(null);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);
    setSubmitError(null);

    const submitData: VolunteerRoleInput = {
      name: formData.name.trim() || null,
      summary: formData.summary.trim() || null,
      responsibilities: formData.responsibilities.trim() || null,
      eligibility: formData.eligibility.trim() || null,
      icon: formData.icon.trim() || null,
      color: formData.color.trim() || null,
      hourlyRate: formData.hourlyRate,
      status: formData.status,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result && !result.didSucceed()) {
      setSubmitError(result);

      return;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as VolunteerRole[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("volunteerRoles:form.editTitle", { defaultValue: "Edit role" })
    : t("volunteerRoles:form.addTitle", { defaultValue: "Add role" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <VolunteerRoleEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("volunteerRoles:form.fields.name.label", { defaultValue: "Name" })}
            description={t("volunteerRoles:form.fields.name.description", {
              defaultValue: "The role's name, shown wherever you staff it.",
            })}
            placeholder={t("volunteerRoles:form.fields.name.placeholder", { defaultValue: "e.g. Route Marshal" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            data-autofocus
            size="md"
          />

          <TextInput
            label={t("volunteerRoles:form.fields.summary.label", { defaultValue: "Summary" })}
            description={t("volunteerRoles:form.fields.summary.description", {
              defaultValue: "A short one-liner shown in lists.",
            })}
            placeholder={t("volunteerRoles:form.fields.summary.placeholder", {
              defaultValue: "e.g. Guides participants at course intersections",
            })}
            value={formData.summary}
            onChange={(e) => setField("summary", e.currentTarget.value)}
            error={fieldErrors.summary}
            size="md"
          />

          <Textarea
            label={t("volunteerRoles:form.fields.responsibilities.label", { defaultValue: "Responsibilities" })}
            description={t("volunteerRoles:form.fields.responsibilities.description", {
              defaultValue: "What the role does. Markdown supported.",
            })}
            placeholder={t("volunteerRoles:form.fields.responsibilities.placeholder", {
              defaultValue: "e.g. Direct participants, watch for hazards, report to the coordinator…",
            })}
            value={formData.responsibilities}
            onChange={(e) => setField("responsibilities", e.currentTarget.value)}
            error={fieldErrors.responsibilities}
            autosize
            minRows={3}
            maxRows={8}
            size="md"
          />

          <Textarea
            label={t("volunteerRoles:form.fields.eligibility.label", { defaultValue: "Eligibility" })}
            description={t("volunteerRoles:form.fields.eligibility.description", {
              defaultValue: "Who qualifies — age, skills or certifications. Markdown supported.",
            })}
            placeholder={t("volunteerRoles:form.fields.eligibility.placeholder", {
              defaultValue: "e.g. 18+, comfortable standing for long periods, first-aid a plus",
            })}
            value={formData.eligibility}
            onChange={(e) => setField("eligibility", e.currentTarget.value)}
            error={fieldErrors.eligibility}
            autosize
            minRows={2}
            maxRows={6}
            size="md"
          />

          <Divider label={t("volunteerRoles:form.sections.pay", { defaultValue: "Pay" })} labelPosition="left" />

          <MoneyInput
            label={t("volunteerRoles:form.fields.hourlyRate.label", { defaultValue: "Hourly rate" })}
            description={
              eventCurrency
                ? t("volunteerRoles:form.fields.hourlyRate.descriptionEvent", {
                    defaultValue: "Leave empty for an unpaid volunteer role. Paid in each event's currency ({{currency}} here); a shift can override it.",
                    currency: eventCurrency,
                  })
                : t("volunteerRoles:form.fields.hourlyRate.description", {
                    defaultValue: "Leave empty for an unpaid volunteer role. Paid in each event's currency; a shift can override it.",
                  })
            }
            placeholder={t("volunteerRoles:form.fields.hourlyRate.placeholder", { defaultValue: "Unpaid" })}
            value={formData.hourlyRate}
            onChange={(v) => setField("hourlyRate", v)}
            currency={eventCurrency}
            hideSymbol={!eventCurrency}
            error={fieldErrors.hourlyRate}
          />

          <Divider label={t("volunteerRoles:form.sections.appearance", { defaultValue: "Appearance" })} labelPosition="left" />

          <Group grow align="flex-start">
            <IconSelector
              label={t("volunteerRoles:form.fields.icon.label", { defaultValue: "Icon" })}
              description={t("volunteerRoles:form.fields.icon.description", {
                defaultValue: "Shown next to the role.",
              })}
              value={formData.icon}
              onChange={(icon) => setField("icon", icon ?? "")}
              error={fieldErrors.icon}
              size="md"
            />

            <ColorPickerInput
              name="color"
              label={t("volunteerRoles:form.fields.color.label", { defaultValue: "Color" })}
              value={formData.color}
              onChange={(_n, v) => setField("color", v ?? "")}
              error={fieldErrors.color}
              size="md"
            />
          </Group>

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
